import type { Messages } from "../fr";

const s = (count: number) => (count === 1 ? "" : "s");

const signals = (count: number) => `${count} ${count === 1 ? "signal" : "signals"}`;

const distance = (days: number) => {
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? "a week ago" : `${weeks} weeks ago`;
};

export const dashboard: Messages["dashboard"] = {
  page: {
    title: "Your workspace — Hune",
    subject: "your overview",
    heading: "Dashboard",
  },
  readOnly: {
    before: "You’re viewing the client workspace ",
    after: " in read-only mode. Switch back to “My workspace” to run a pass.",
  },
  activity: {
    title: (days: number) => `Activity over the last ${days} days`,
    comparison: (days: number) => `Compared with the previous ${days} days`,
    passes: "Passes",
    passesKept: "Kept with their sources",
    passesEmpty: "Your history will show up here",
    opportunities: "Opportunities found",
    sources: (count: number) => `${count} distinct source${s(count)}`,
    sourcesEmpty: "Every signal stays available",
    leads: "Leads added to memory",
    pipelineTotal: (count: number) => `${count} in the pipeline in total`,
    personalOnly: "Personal workspace only",
  },
  statTile: {
    stable: "unchanged",
    versus: (delta: string, days: number) => `${delta} vs prev. ${days} days`,
    comparedTo: (days: number) => `Compared with the previous ${days} days`,
  },
  runs: {
    title: "Latest passes",
    history: "Full history",
    empty: "No passes recorded yet.",
    launchFirst: "Run the first one",
    noOpportunity: "No opportunities",
    opportunities: (count: number) => `${count} opportunit${count === 1 ? "y" : "ies"}`,
    sources: { radar: "monitoring", manual: "manual" },
  },
  pipeline: {
    title: "Pipeline",
    open: "Open",
    personalOnly: "The pipeline is shown from your personal workspace.",
    empty: "No leads yet. They’ll arrive with your next analysis.",
    stages: {
      fresh: "Not opened",
      toContact: "To contact",
      engaged: "In conversation",
      won: "Won",
      other: "Other",
    },
    stageCount: (count: number, stage: string) => `${count} ${stage.toLowerCase()}`,
    breakdown: (total: number, parts: string[]) => `Breakdown of ${total} leads: ${parts.join(", ")}.`,
    tracked: (count: number) => `${count} lead${s(count)} tracked`,
    openUnread: (count: number) => `Open ${count} unread`,
  },
  passSummary: {
    label: "Summary of the last pass",
    completed: "Pass complete",
    last: "Last pass",
    details: "Pass details",
    newLeads: (count: number) => (count === 1 ? "new lead" : "new leads"),
    reactivated: () => "reactivated",
    known: () => "already known",
    nothingNew: (count: number) =>
      `${signals(count)} spotted, no new leads: the market hasn’t produced anything new since the previous pass.`,
    sourced: (count: number) => `${signals(count)} sourced by this pass, already attached to leads in the pipeline.`,
  },
  watch: {
    errors: {
      generic: "Something went wrong.",
      follow: "Unable to follow the pass.",
      failed: "The pass failed.",
      tooLong: "The pass is taking longer than expected. The report will appear in your passes.",
      start: "Unable to start the pass.",
      noReport: "The agent didn’t return a report.",
      saveWatch: "Unable to save monitoring.",
    },
    status: {
      busy: { title: "Pass in progress", note: "the agent is exploring your market" },
      error: { title: "Pass interrupted", note: "you can run it again" },
      outOfPlan: "Not in your plan",
      active: { title: "Monitoring on", note: (cadence: string) => `a pass ${cadence}` },
      paused: { title: "Monitoring paused", note: "no automatic passes" },
      available: { title: "Agent ready", note: "ready for the first analysis" },
    },
    distance,
    lastPass: (days: number, nextDay: string | null) =>
      `Last pass ${distance(days)}${nextDay ? ` · next on ${nextDay}` : ""}`,
    firstPass: (day: string) => `First pass on ${day}`,
    noPass: "No passes yet",
    toggle: "Automatic monitoring",
    unread: (count: number) => `See ${count} unread lead${s(count)}`,
    openPipeline: "Open the pipeline",
    cooldownTitle: (hours: number) => `A pass just ran — you can run another in ${hours} h`,
    searchNowTitle: "Run a pass now",
    running: "Pass in progress",
    searchNow: "Search now",
    offersTitle: "Targeting, cadence and monitored offers",
    offers: "My offers",
    first: {
      label: "First analysis",
      intro:
        "Enter your website’s address: the agent infers your offer and your target from it, then monitors the market for you. No source is made up, no message is sent, and a pass takes 5 to 10 minutes.",
      placeholder: "your-product.com",
      analyzing: "Analysis in progress",
      launch: "Run the first analysis",
      demo: "See a demo",
    },
    background: "You can switch tabs or close this one: the pass keeps going and its leads will be waiting in the pipeline.",
    cooldown: (days: number | null, hours: number) =>
      `A pass ran ${days === null ? "recently" : distance(days)}: the agent never flags a known fact twice, so it wouldn’t find anything new for another ${hours} h.`,
    searchAnyway: "Search anyway",
  },
  notifyMe: {
    enabled: "You’ll get a notification when the pass ends.",
    ask: "Notify me when it’s done",
  },
  radar: {
    families: {
      network: "Professional network",
      hiring: "Hiring",
      procurement: "Tenders",
      press: "Press",
      registries: "Registries",
      regulation: "Regulation",
      events: "Events",
      communities: "Communities",
    },
    label: (searches: number, pages: number) => `Sources explored by the agent: ${searches} searches, ${pages} pages read`,
    pages: (count: number) => `${count} page${s(count)}`,
    searches: (count: number) => `${count} search${count === 1 ? "" : "es"}`,
    now: {
      connecting: "Connecting to the agent…",
      queued: "Queued: the agent will start in a moment",
      understand: "Reading your offer and your market",
      searching: (query: string) => `Searching “${query}”`,
      reading: (host: string) => `Reading ${host}`,
      exploring: "Exploring sources",
      filing: "Filing leads into your pipeline",
      writing: "Cross-checking sources and writing the report",
      contacts: "Looking for the right contacts",
      leads: (count: number) => `${count} new or reactivated lead${s(count)}`,
      nothingNew: "Pass complete: nothing new this time",
      failed: "The pass was interrupted.",
    },
    steps: {
      understand: "Understand the offer",
      explore: "Explore sources",
      retain: "Keep the signals",
      contacts: "Find the contacts",
    },
    notes: {
      queued: "queued",
      retained: (count: number) => `${count} kept`,
      writing: "writing",
      enriched: (done: number, total: number) => `${done} of ${total}`,
    },
    totals: { searches: "searches", pages: "pages read", leads: "leads" },
    current: "Right now",
    outcome: "Summary",
  },
  passLeads: {
    title: "Leads from this pass",
    openPipeline: "Open the pipeline",
  },
  errors: {
    signIn: "Please log in.",
    invalidId: "Invalid identifier.",
    signInToFollow: "Log in to follow a pass.",
    passNotFound: "Pass not found.",
  },
};
